// ts-rest contract for the View per-page typed endpoints.
// Firm-overview returns the compliance / breach / KYC rows whose counts the
// page subtitle reports (breach + KYC now read through ctx.read). The
// view_aggregates / dashboard_aggregates read-model caches were dropped
// (#151, ADR-0008): the financial/investment/board pages return empty until
// their ObjectTypes are modelled.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionOkSchema, staffActionErrorSchema } from './agent';

const c = initContract();

// `passthrough` is now reserved ONLY for the unmodelled view-aggregate rows
// (financial / investment / board performance) whose ObjectTypes are not yet
// modelled (#151) — those endpoints return an empty `rows: []`. The
// fund-management / trust-detail rows below are REAL camelCase DTOs: the
// service projects the typed Drizzle rows onto them via `toDto` mappers, so
// snake_case stays in the DB layer (the Drizzle column names) and never reaches
// the wire (PRD #278: contract fields are camelCase).
const passthrough = z.record(z.unknown());

// --- Fund-management row DTOs ------------------------------------------------
// camelCase wire keys mirror the Drizzle TS-property keys; the service `toDto`
// mappers drop the snake_case column aliases the legacy `table-rows` row shim
// used to dual-key. Field sets are the columns the fund-management screens read.

export const investmentTrustRowSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    ticker: z.string().optional(),
    peerGroup: z.string().optional(),
    reportingCurrency: z.string().optional(),
    benchmark: z.string().nullable().optional(),
    listedSince: z.string().optional(),
});
export type InvestmentTrustRow = z.infer<typeof investmentTrustRowSchema>;

export const exposureSnapshotRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    asOf: z.string(),
    navPerShare: z.number(),
    sharePrice: z.number(),
    discountToNavPct: z.number(),
    gearingPct: z.number(),
    sectorExposure: z.array(z.object({ sector: z.string(), weightPct: z.number() })),
    topHoldingConcentrationPct: z.number(),
    topHoldingSecurityName: z.string(),
    shareholderConcentrationTop5Pct: z.number(),
});
export type ExposureSnapshotRow = z.infer<typeof exposureSnapshotRowSchema>;

export const discountSnapshotRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    asOf: z.string(),
    navPerShare: z.number(),
    sharePrice: z.number(),
    discountToNavPct: z.number(),
    peerGroupDiscountPct: z.number().nullable().optional(),
});
export type DiscountSnapshotRow = z.infer<typeof discountSnapshotRowSchema>;

export const gearingSnapshotRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    asOf: z.string(),
    netGearingPct: z.number(),
    policyLimitPct: z.number(),
    covenantLimitPct: z.number(),
    facilityLimitGbp: z.number(),
    facilityDrawnGbp: z.number(),
    costOfDebtPct: z.number(),
    cashPct: z.number(),
});
export type GearingSnapshotRow = z.infer<typeof gearingSnapshotRowSchema>;

export const policyClauseRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    clauseRef: z.string(),
    ruleKind: z.string(),
    title: z.string(),
    statement: z.string(),
    metric: z.string(),
    subject: z.string().nullable().optional(),
    direction: z.enum(['max', 'min', 'band']),
    limitPct: z.number().nullable(),
    lowerBoundPct: z.number().nullable(),
    upperBoundPct: z.number().nullable(),
    breachSeverity: z.enum(['approaching', 'breach', 'material']),
    sourceDocument: z.string(),
    effectiveFrom: z.string(),
});
export type PolicyClauseRow = z.infer<typeof policyClauseRowSchema>;

export const shareholderRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    holderName: z.string(),
    // holderType values are domain enum strings stored in the DB (snake_case
    // values are agreed wire values, not casing-drift keys).
    holderType: z.enum(['institutional', 'retail', 'wealth_manager', 'insider']),
    holdingPct: z.number(),
    sharesHeld: z.number(),
    changeQoqPct: z.number(),
    notifiable: z.boolean(),
    asOf: z.string(),
});
export type ShareholderRow = z.infer<typeof shareholderRowSchema>;

export const continuationVoteRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    voteDate: z.string(),
    cadenceYears: z.number(),
    approvalThresholdPct: z.number(),
    discountTriggerPct: z.number().nullable().optional(),
    status: z.enum(['scheduled', 'in_campaign', 'passed', 'failed']),
    forecastOutcome: z.enum(['pass_likely', 'pass_marginal', 'at_risk']).nullable().optional(),
    forecastSupportPct: z.number().nullable().optional(),
    notes: z.string().nullable().optional(),
});
export type ContinuationVoteRow = z.infer<typeof continuationVoteRowSchema>;

export const brokerCoverageRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    broker: z.string(),
    analyst: z.string(),
    rating: z.enum(['buy', 'add', 'hold', 'reduce', 'sell']),
    targetPrice: z.number(),
    targetPriceHistory: z.array(z.object({ asOf: z.string(), targetPrice: z.number() })),
    lastNoteTitle: z.string(),
    lastNoteDate: z.string(),
    isHouseBroker: z.boolean(),
});
export type BrokerCoverageRow = z.infer<typeof brokerCoverageRowSchema>;

export const fundReportDraftRowSchema = z.object({
    id: z.string(),
    investmentTrustId: z.string(),
    kind: z.string(),
    title: z.string().optional(),
    summary: z.string().optional(),
    body: z.string().optional(),
    reviewStatus: z
        .enum(['pending', 'approved', 'rejected', 'revision_requested'])
        .nullable()
        .optional(),
});
export type FundReportDraftRow = z.infer<typeof fundReportDraftRowSchema>;

// --- Firm Overview row DTOs (tight — server projects ontology rows to these) -
// The page subtitle counts open breaches, overdue KYC and open compliance
// alerts; these are the minimal honest shapes the controller projects (the
// alert vocabulary is mapped to the display strings the page renders).
export const firmOverviewBreachSchema = z.object({
    status: z.string(),
});
export const firmOverviewKycReviewSchema = z.object({
    status: z.string(),
    daysUntilDue: z.number(),
});
export const firmOverviewComplianceAlertSchema = z.object({
    alertType: z.string(),
    status: z.string(),
});

export const firmOverviewResponseSchema = z.object({
    breachRegister: z.array(firmOverviewBreachSchema),
    kycReviews: z.array(firmOverviewKycReviewSchema),
    complianceAlerts: z.array(firmOverviewComplianceAlertSchema),
});
export type FirmOverviewResponse = z.infer<typeof firmOverviewResponseSchema>;
export type FirmOverviewBreach = z.infer<typeof firmOverviewBreachSchema>;
export type FirmOverviewKycReview = z.infer<typeof firmOverviewKycReviewSchema>;
export type FirmOverviewComplianceAlert = z.infer<typeof firmOverviewComplianceAlertSchema>;

export const viewAggregatesResponseSchema = z.object({
    rows: z.array(passthrough),
});
export type ViewAggregatesResponse = z.infer<typeof viewAggregatesResponseSchema>;

export const fundManagementListResponseSchema = z.object({
    trusts: z.array(investmentTrustRowSchema),
    exposures: z.array(exposureSnapshotRowSchema),
    discounts: z.array(discountSnapshotRowSchema),
    gearing: z.array(gearingSnapshotRowSchema),
});
export type FundManagementListResponse = z.infer<typeof fundManagementListResponseSchema>;

// --- Fund-management row / mutation-result DTOs (tight) ----------------------
// `rnsAnnouncementDtoSchema` is both the trust-detail RNS read projection AND
// the materialise mutation's result row. Both mutations dispatch through
// ontology Actions (ADR-0008) and report the row they materialised inside the
// generic `StaffActionResult.data` envelope. The envelope is loose by design
// (ADR-0018), but the rows inside it are typed ontology projections — these
// schemas mirror what the server sends so the frontend gets the real shape
// rather than `Record<string, unknown>`. Declared before the response schema
// that embeds them.

/** The durable RnsAnnouncement — the trust-detail read projection and the
 *  materialise mutation's result row. camelCase wire keys mirror the ontology
 *  RnsAnnouncement row directly (PRD #278: contract fields are camelCase). */
export const rnsAnnouncementDtoSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    kind: z.string(),
    headline: z.string(),
    body: z.string(),
    status: z.enum(['drafted', 'approved', 'dispatched', 'withdrawn']),
    originatedFromDraftId: z.string().nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: z.string().nullable(),
    dispatchedAt: z.string().nullable(),
    rnsReference: z.string().nullable(),
    distributor: z.string().nullable(),
    createdAt: z.string(),
});
export type RnsAnnouncementDto = z.infer<typeof rnsAnnouncementDtoSchema>;

/** The InvestmentTrustPolicyBreach the approve-materialise mutation creates
 *  (camelCase wire keys mirroring the ontology row). */
export const itpBreachDtoSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    ruleKind: z.string(),
    severity: z.enum(['approaching', 'breach', 'material']),
    rnsTrigger: z.boolean(),
    remediationPlan: z.string(),
    raisedAt: z.string(),
    status: z.string(),
    originFindingId: z.string(),
});
export type ItpBreachDto = z.infer<typeof itpBreachDtoSchema>;

export const trustDetailResponseSchema = z.object({
    trust: investmentTrustRowSchema.nullable(),
    exposures: z.array(exposureSnapshotRowSchema),
    policyClauses: z.array(policyClauseRowSchema),
    gearing: z.array(gearingSnapshotRowSchema),
    discounts: z.array(discountSnapshotRowSchema),
    shareholders: z.array(shareholderRowSchema),
    continuationVotes: z.array(continuationVoteRowSchema),
    brokerCoverage: z.array(brokerCoverageRowSchema),
    // RnsAnnouncement is an ontology Object Type read via ctx.read and projected
    // by the service — a real schema, not an ingestion-source passthrough.
    rnsAnnouncements: z.array(rnsAnnouncementDtoSchema),
    fundReportDrafts: z.array(fundReportDraftRowSchema),
});
export type TrustDetailResponse = z.infer<typeof trustDetailResponseSchema>;

const errorBody = z.object({ error: z.string() });

// Risk Register (view/risk-register) — enterprise risk items aggregated from
// the breach register, financial-crime alerts and suitability alerts.
export const riskItemDtoSchema = z.object({
    id: z.string(),
    category: z.enum(['Regulatory', 'Conduct', 'Financial Crime', 'Operational', 'Client']),
    title: z.string(),
    description: z.string(),
    severity: z.enum(['High', 'Medium', 'Low']),
    status: z.enum(['Open', 'Under review', 'Mitigated', 'Closed']),
    owner: z.string(),
    source: z.string(),
    raisedAt: z.string(),
});
export type RiskItemDto = z.infer<typeof riskItemDtoSchema>;

export const riskRegisterResponseSchema = z.object({
    stats: z.object({
        totalOpen: z.number(),
        high: z.number(),
        medium: z.number(),
        low: z.number(),
    }),
    byCategory: z.array(z.object({ category: z.string(), total: z.number(), high: z.number() })),
    risks: z.array(riskItemDtoSchema),
});
export type RiskRegisterResponse = z.infer<typeof riskRegisterResponseSchema>;

// Operational Intelligence (view/operational-intelligence) — operational signals
// aggregated from the reconciliation, settlement, cash and corporate-action
// queues (firm-level MI counts).
export const opsInsightDtoSchema = z.object({
    id: z.string(),
    category: z.enum(['Reconciliation', 'Settlement', 'Cash', 'Corporate actions', 'Capacity']),
    priority: z.enum(['High', 'Medium', 'Low']),
    title: z.string(),
    detail: z.string(),
    metric: z.string(),
    recommendation: z.string(),
});
export type OpsInsightDto = z.infer<typeof opsInsightDtoSchema>;

export const operationalIntelligenceResponseSchema = z.object({
    stats: z.object({
        openReconBreaks: z.number(),
        failedTrades: z.number(),
        overdrawnAccounts: z.number(),
        pendingElections: z.number(),
    }),
    insights: z.array(opsInsightDtoSchema),
});
export type OperationalIntelligenceResponse = z.infer<typeof operationalIntelligenceResponseSchema>;

// --- Financial Performance (view/financial-performance) ----------------------
// Firm-level revenue / fee-mix MI DERIVED server-side from the Fee and
// PortfolioPerformanceSnapshot Object Types (read-only). All figures GBP.
export const financialRevenueRowSchema = z.object({
    id: z.string(),
    // Fee type display label (e.g. "Management fees").
    type: z.string(),
    current: z.number(),
    previous: z.number(),
    changePct: z.number(),
});
export type FinancialRevenueRow = z.infer<typeof financialRevenueRowSchema>;

export const financialConcentrationRowSchema = z.object({
    id: z.string(),
    rank: z.number(),
    client: z.string(),
    revenue: z.number(),
    sharePct: z.number(),
    cumulativePct: z.number(),
});
export type FinancialConcentrationRow = z.infer<typeof financialConcentrationRowSchema>;

export const financialSegmentRowSchema = z.object({
    id: z.string(),
    segment: z.string(),
    clients: z.number(),
    aum: z.number(),
    revenue: z.number(),
    // Blended fee rate as a fraction of AUM (e.g. 0.0085 == 0.85%).
    blendedFeeBps: z.number(),
});
export type FinancialSegmentRow = z.infer<typeof financialSegmentRowSchema>;

export const financialPerformanceResponseSchema = z.object({
    stats: z.object({
        totalRevenue: z.number(),
        totalAum: z.number(),
        // Blended firm fee in basis points (revenue / AUM * 10000).
        blendedFeeBps: z.number(),
        revenueChangePct: z.number(),
    }),
    revenue: z.array(financialRevenueRowSchema),
    bySegment: z.array(financialSegmentRowSchema),
    concentration: z.array(financialConcentrationRowSchema),
});
export type FinancialPerformanceResponse = z.infer<typeof financialPerformanceResponseSchema>;

// --- Investment Performance (view/investment-performance) --------------------
// Model-portfolio returns vs benchmark DERIVED from PortfolioPerformanceSnapshot
// rows, grouped into house models by client risk band. Contributors are the
// firm's most-held securities by valuation (ClientHolding).
export const modelPerformanceRowSchema = z.object({
    id: z.string(),
    model: z.string(),
    ytdPct: z.number(),
    oneYearPct: z.number(),
    threeYearPct: z.number(),
    benchmark: z.string(),
    benchmarkYtdPct: z.number(),
    // Active return vs benchmark (ytd - benchmark), percentage points.
    vsBenchmarkPct: z.number(),
    risk: z.enum(['Low', 'Low-Medium', 'Medium', 'Medium-High', 'High']),
    aum: z.number(),
});
export type ModelPerformanceRow = z.infer<typeof modelPerformanceRowSchema>;

export const contributorRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    // Contribution to return in percentage points (signed).
    contributionPct: z.number(),
    // Firm-wide held value (GBP), display-only.
    heldValue: z.number(),
});
export type ContributorRow = z.infer<typeof contributorRowSchema>;

export const investmentPerformanceResponseSchema = z.object({
    stats: z.object({
        // AUM-weighted firm YTD return, percentage.
        firmYtdPct: z.number(),
        // AUM-weighted firm active return vs benchmark, percentage points.
        firmVsBenchmarkPct: z.number(),
        bestModel: z.string(),
        totalAum: z.number(),
    }),
    models: z.array(modelPerformanceRowSchema),
    topContributors: z.array(contributorRowSchema),
    bottomContributors: z.array(contributorRowSchema),
});
export type InvestmentPerformanceResponse = z.infer<typeof investmentPerformanceResponseSchema>;

// --- Custodian Analytics (view/custodian-analytics) --------------------------
// Custodian cost / service MI: AUM per custodian DERIVED from ClientAccount +
// ClientHolding (custodian grouping); SLA / cost metrics are plausible canned
// per-custodian constants (no live custodian-cost feed — ADR-0005).
export const custodianRowSchema = z.object({
    id: z.string(),
    custodian: z.string(),
    aum: z.number(),
    accounts: z.number(),
    holdings: z.number(),
    sharePct: z.number(),
    // Annual custody cost in basis points of AUM (canned per-custodian).
    costBps: z.number(),
    // Settlement SLA — % settled on the contractual date (canned).
    settlementSlaPct: z.number(),
    // Corporate-action notification SLA — % within agreed window (canned).
    caSlaPct: z.number(),
    // Reconciliation breaks raised against this custodian in the period (canned).
    breaks: z.number(),
    rating: z.enum(['Excellent', 'Good', 'Adequate', 'Watch']),
});
export type CustodianRow = z.infer<typeof custodianRowSchema>;

export const custodianAnalyticsResponseSchema = z.object({
    stats: z.object({
        totalAum: z.number(),
        custodianCount: z.number(),
        // AUM-weighted blended custody cost (bps).
        blendedCostBps: z.number(),
        // AUM-weighted settlement SLA (%).
        weightedSettlementSlaPct: z.number(),
    }),
    custodians: z.array(custodianRowSchema),
});
export type CustodianAnalyticsResponse = z.infer<typeof custodianAnalyticsResponseSchema>;

// --- Business Development (view/business-development) -------------------------
// Prospect pipeline DERIVED from the OnboardingPipelineCase Object Type
// (read-only): stage funnel + source mix + weighted pipeline value.
export const bdStageRowSchema = z.object({
    id: z.string(),
    stage: z.string(),
    cases: z.number(),
    value: z.number(),
    // Indicative conversion probability at this stage (canned funnel weight).
    probabilityPct: z.number(),
    weightedValue: z.number(),
});
export type BdStageRow = z.infer<typeof bdStageRowSchema>;

export const bdSourceRowSchema = z.object({
    id: z.string(),
    source: z.string(),
    cases: z.number(),
    value: z.number(),
    sharePct: z.number(),
});
export type BdSourceRow = z.infer<typeof bdSourceRowSchema>;

export const businessDevelopmentResponseSchema = z.object({
    stats: z.object({
        activeProspects: z.number(),
        pipelineValue: z.number(),
        weightedPipelineValue: z.number(),
        wonThisYear: z.number(),
        wonValueThisYear: z.number(),
    }),
    stages: z.array(bdStageRowSchema),
    sources: z.array(bdSourceRowSchema),
});
export type BusinessDevelopmentResponse = z.infer<typeof businessDevelopmentResponseSchema>;

// --- Benchmarking (view/benchmarking) ----------------------------------------
// The firm's own metrics (DERIVED) shown against plausible peer-group constants.
export const benchmarkMetricRowSchema = z.object({
    id: z.string(),
    metric: z.string(),
    unit: z.enum(['gbp', 'percent', 'bps', 'count', 'ratio']),
    firmValue: z.number(),
    peerMedian: z.number(),
    peerTopQuartile: z.number(),
    // Higher firm value is better for this metric (drives RAG direction).
    higherIsBetter: z.boolean(),
});
export type BenchmarkMetricRow = z.infer<typeof benchmarkMetricRowSchema>;

export const benchmarkingResponseSchema = z.object({
    peerGroup: z.string(),
    asOf: z.string(),
    metrics: z.array(benchmarkMetricRowSchema),
});
export type BenchmarkingResponse = z.infer<typeof benchmarkingResponseSchema>;

// --- Regulatory Calendar (view/reg-calendar) ---------------------------------
// Forward list of standard FCA / regulatory filing deadlines (canned constant
// list keyed off the firm's reporting cadence — no live filing feed, ADR-0005).
export const regFilingRowSchema = z.object({
    id: z.string(),
    ref: z.string(),
    name: z.string(),
    authority: z.string(),
    frequency: z.enum(['Monthly', 'Quarterly', 'Semi-annual', 'Annual', 'Ad hoc']),
    dueDate: z.string(),
    owner: z.string(),
    status: z.enum(['Upcoming', 'Due soon', 'Overdue', 'Submitted']),
    daysUntilDue: z.number(),
});
export type RegFilingRow = z.infer<typeof regFilingRowSchema>;

export const regCalendarResponseSchema = z.object({
    stats: z.object({
        dueSoon: z.number(),
        overdue: z.number(),
        submittedYtd: z.number(),
    }),
    filings: z.array(regFilingRowSchema),
});
export type RegCalendarResponse = z.infer<typeof regCalendarResponseSchema>;

export const viewPagesContract = c.router(
    {
        getRiskRegister: {
            method: 'GET',
            path: '/view/risk-register',
            responses: { 200: riskRegisterResponseSchema, 403: errorBody },
            summary: 'Enterprise risk register derived from breaches / alerts.',
        },
        getOperationalIntelligence: {
            method: 'GET',
            path: '/view/operational-intelligence',
            responses: { 200: operationalIntelligenceResponseSchema, 403: errorBody },
            summary: 'Operational signals from recon / settlement / cash / CA queues.',
        },
        getFirmOverview: {
            method: 'GET',
            path: '/view/firm-overview',
            responses: { 200: firmOverviewResponseSchema, 403: errorBody },
            summary: 'Firm Overview composed payload',
        },
        getFinancialPerformance: {
            method: 'GET',
            path: '/view/financial-performance',
            responses: { 200: financialPerformanceResponseSchema, 403: errorBody },
            summary: 'Financial Performance — revenue / fee-mix MI derived from fees + AUM.',
        },
        getInvestmentPerformance: {
            method: 'GET',
            path: '/view/investment-performance',
            responses: { 200: investmentPerformanceResponseSchema, 403: errorBody },
            summary: 'Investment Performance — model returns vs benchmark derived from snapshots.',
        },
        getCustodianAnalytics: {
            method: 'GET',
            path: '/view/custodian-analytics',
            responses: { 200: custodianAnalyticsResponseSchema, 403: errorBody },
            summary: 'Custodian Analytics — AUM by custodian (derived) + cost/SLA MI.',
        },
        getBusinessDevelopment: {
            method: 'GET',
            path: '/view/business-development',
            responses: { 200: businessDevelopmentResponseSchema, 403: errorBody },
            summary: 'Business Development — prospect pipeline derived from onboarding cases.',
        },
        getBenchmarking: {
            method: 'GET',
            path: '/view/benchmarking',
            responses: { 200: benchmarkingResponseSchema, 403: errorBody },
            summary: 'Benchmarking — firm metrics (derived) vs peer-group constants.',
        },
        getRegCalendar: {
            method: 'GET',
            path: '/view/reg-calendar',
            responses: { 200: regCalendarResponseSchema, 403: errorBody },
            summary: 'Regulatory Calendar — forward list of standard filing deadlines.',
        },
        getBoardReporting: {
            method: 'GET',
            path: '/view/board-reporting',
            responses: { 200: viewAggregatesResponseSchema, 403: errorBody },
            summary: 'Board Reporting — dashboard_aggregates pass-through',
        },
        getFundManagementList: {
            method: 'GET',
            path: '/view/fund-management/list',
            responses: { 200: fundManagementListResponseSchema, 403: errorBody },
            summary: 'Fund Management top-level grid composed payload',
        },
        getTrustDetail: {
            method: 'GET',
            path: '/view/fund-management/:trustId',
            pathParams: z.object({ trustId: z.string() }),
            responses: { 200: trustDetailResponseSchema, 403: errorBody },
            summary: 'Per-trust detail composed for all subscreens',
        },

        // ------------------------------------------------------------------
        // Fund-management mutations (OLD_CODE.html #2 replacement for
        // `materialise-rns-announcement`, `submit-rns-announcement`,
        // `approve-investment-trust-policy-finding-materialise`). Both dispatch
        // through ontology Actions (ADR-0008): MaterialiseRnsAnnouncement and
        // ApproveInvestmentTrustPolicyFinding respectively.
        // ------------------------------------------------------------------
        materialiseRnsAnnouncement: {
            method: 'POST',
            path: '/view/fund-management/rns/materialise',
            body: z.object({ draftId: z.string(), approvedBy: z.string().optional() }),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Materialise an RnsAnnouncement row from an approved CHARTER FundReportDraft.',
        },
        approveInvestmentTrustPolicyFindingMaterialise: {
            method: 'POST',
            path: '/view/fund-management/policy-findings/:id/approve-materialise',
            pathParams: z.object({ id: z.string() }),
            // The endpoint collects the real workflow decisions the
            // ApproveInvestmentTrustPolicyFinding Action accepts (ADR-0018
            // write-side rule): the reviewer may override the proposed severity
            // / RNS-trigger and attach an approval note. Omitted → the Action
            // falls back to the finding's proposed values.
            body: z
                .object({
                    severityOverride: z.enum(['approaching', 'breach', 'material']).optional(),
                    rnsTriggerOverride: z.boolean().optional(),
                    approvalNote: z.string().optional(),
                })
                .optional(),
            responses: {
                200: staffActionOkSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary:
                'Approve a BEACON InvestmentTrustPolicyFinding and materialise the durable breach row (ADR-0003).',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
